import qs from "qs";
import React, { useEffect, useRef } from "react";
import { Redirect } from "react-router";
import { useHistory, useLocation } from "react-router-dom";

import { UrlProvider } from "../../../../../api/UrlProvider";
import withLoginOtpPage from "../../../../../hocs/withLoginOtpPage";
import { OTP_FORM_FIELD } from "../../../../../lib/auth/authOtpConstants";
import {
  getLoginWithRedirectUrl,
  getRegisterWithRedirectUrl,
  resolvePostAuthRedirectPath
} from "../../../../../lib/auth/authRedirectUrl";
import { hasLoginOtpNavigationState } from "../../../../../lib/auth/loginOtpNavigationState";
import { useLoginOtpResendCooldown } from "../../../../../hooks/useLoginOtpResendCooldown";
import useLoginOtpFragmentHash from "../../../../hooks/useLoginOtpFragmentHash";
import AuthPageLayout from "../../../auth/AuthPageLayout";
import InvisibleReCaptcha from "../../../common/InvisibleReCaptcha";
import Loader from "../../../common/Loader";
import "../../../auth/authForm.scss";
import OtpCodeForm from "../components/OtpCodeForm";
import OtpUserChangeRow from "../components/OtpUserChangeRow";
import LoginOtpHash from "../components/LoginOtpHash";

function NewLoginOtpPage({
  formBuilder,
  userData,
  locale,
  translate,
  verifyLoading,
  otpRequestLoading,
  otpLastSentAt,
  validationErrors,
  clearValidationError: clearFieldValidationError,
  verifyOtp,
  otpRequest
}) {
  const { state, search } = useLocation();
  const history = useHistory();
  const reCaptchaRef = useRef(null);
  const resendCooldownSeconds = useLoginOtpResendCooldown(otpLastSentAt);

  const user = state?.user;
  const fromRegisterFromState = Boolean(state?.fromRegister);
  const { hash, fromRegister: fromRegisterFromHash } =
    useLoginOtpFragmentHash();
  const fromRegister = fromRegisterFromState || fromRegisterFromHash;
  const isHashMode = Boolean(hash);
  const loginHref = UrlProvider.getUrl("fe.login", { locale });
  const params = qs.parse(search, { ignoreQueryPrefix: true });
  const redirectTo =
    typeof params.redirectTo === "string" ? params.redirectTo : "";
  const changeAccountTo = fromRegister
    ? getRegisterWithRedirectUrl({
        locale,
        returnUrl: redirectTo,
        toastMessage: params.toastMessage
      })
    : redirectTo
      ? getLoginWithRedirectUrl({ locale, returnUrl: redirectTo })
      : loginHref;

  useEffect(() => {
    if (!userData) {
      return;
    }

    history.replace(resolvePostAuthRedirectPath(search));
  }, [userData, history, search]);

  if (userData) {
    return <Loader />;
  }

  if (!isHashMode && (!user || !hasLoginOtpNavigationState(state))) {
    return <Redirect to={changeAccountTo} />;
  }

  if (isHashMode) {
    return (
      <LoginOtpHash
        hash={hash}
        fromRegister={fromRegister}
        userData={userData}
        loginHref={loginHref}
        translate={translate}
      />
    );
  }

  const handleSubmit = async () => {
    const code = formBuilder.fields.find(
      field => field.name === OTP_FORM_FIELD.CODE
    )?.value;
    const recaptcha = await reCaptchaRef.current?.executeAsync?.();

    if (!recaptcha) {
      return;
    }

    verifyOtp({
      user,
      code,
      recaptcha,
      fromRegister
    });
  };

  const handleResend = async () => {
    if (resendCooldownSeconds > 0 || otpRequestLoading || verifyLoading) {
      return;
    }

    const recaptcha = await reCaptchaRef.current?.executeAsync?.();

    if (!recaptcha) {
      return;
    }

    const otpRequestPayload = {
      user,
      recaptcha,
      fromRegister
    };

    if (typeof params.redirectTo === "string" && params.redirectTo.length > 0) {
      otpRequestPayload.redirectTo = params.redirectTo;
    }

    otpRequest(otpRequestPayload);
  };

  const resendDisabled =
    resendCooldownSeconds > 0 || otpRequestLoading || verifyLoading;

  return (
    <AuthPageLayout
      title={translate("auth_otp-page_title")}
      subtitle={
        <>
          <p className={"text-xs line-height-160 text-black-8 mb-4"}>
            {translate("auth_otp-page_subtitle")}
          </p>
          <OtpUserChangeRow
            user={user}
            changeAccountLabel={translate("auth_otp-page_change-account")}
            onChangeAccount={() => {
              history.push(changeAccountTo);
            }}
          />
        </>
      }
    >
      <OtpCodeForm
        formBuilder={formBuilder}
        validationErrors={validationErrors}
        clearValidationError={clearFieldValidationError}
        translate={translate}
        verifyLoading={verifyLoading}
        otpRequestLoading={otpRequestLoading}
        resendDisabled={resendDisabled}
        onSubmit={handleSubmit}
        onResend={handleResend}
        resendCooldownSeconds={resendCooldownSeconds}
      />
      <InvisibleReCaptcha ref={reCaptchaRef} />
    </AuthPageLayout>
  );
}

export default withLoginOtpPage(NewLoginOtpPage);
